import { forwardRef, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { cn } from "../utils/cn";
import { navbarLinks } from "../constants";
import logoLight from "../../assets/logo.jpg";
import logoDark from "../../assets/logo.jpg";
import PropTypes from "prop-types";
import { fetchAdminTabs } from "../api/tabsApi";
import * as LucideIcons from "lucide-react";
import { toast } from "react-toastify";

export const Sidebar = forwardRef(({ collapsed, setCollapsed }, ref) => {
  const location = useLocation();
  const [tabs, setTabs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Map icon names to Lucide React icons
  const iconMap = {
    Home: LucideIcons.Home,
    LibraryBig: LucideIcons.LibraryBig,
    Box: LucideIcons.Box,
    Users: LucideIcons.Users,
    BriefcaseBusiness: LucideIcons.BriefcaseBusiness,
    Medal: LucideIcons.Medal,
    Settings: LucideIcons.Settings,
    ChartColumn: LucideIcons.ChartColumn,
    NotepadText: LucideIcons.NotepadText,
  };

  useEffect(() => {
    const loadTabs = async () => {
      try {
        setLoading(true);
        const tabsData = await fetchAdminTabs();
        
        // Only proceed if we have tabs
        if (!tabsData || !tabsData.tabs || tabsData.tabs.length === 0) {
          toast.warn("No Access sections avaible for u!")
          setTabs([]); // Set empty array, don't fallback to navbarLinks
          return;
        }

        // Convert API response to navbarLinks format
        const groupedTabs = {};
        
        if (tabsData.tabsBySection) {
          Object.keys(tabsData.tabsBySection).forEach((section) => {
            groupedTabs[section] = tabsData.tabsBySection[section].map((tab) => ({
              label: tab.label,
              path: tab.path,
              icon: iconMap[tab.icon] || LucideIcons.Home, // Fallback to Home icon
            }));
          });
        }
        
        // Convert grouped tabs to navbarLinks format
        const formattedTabs = Object.keys(groupedTabs).map((section) => ({
          title: section,
          links: groupedTabs[section],
        }));
        
        setTabs(formattedTabs);
      } catch (error) {
        console.error("Failed to load tabs:", error);
        // Don't fallback to navbarLinks on error - show no tabs instead
        setTabs([]);
      } finally {
        setLoading(false);
      }
    };

    loadTabs();
  }, []);

  const handleLinkClick = () => {
    // Only collapse the sidebar on mobile devices
    if (window.innerWidth < 768) {
      setCollapsed(true);
    }
  };

  const displayTabs = tabs.length > 0 ? tabs : [];

  return (
    <aside
      ref={ref}
      className={cn(
        "fixed z-50 flex h-full flex-col overflow-x-hidden border-r border-gray-200 bg-white shadow-sm transition-all duration-300 ease-in-out dark:border-gray-700 dark:bg-gray-900",
        collapsed ? "md:w-20 md:items-center" : "md:w-64",
        collapsed ? "max-md:-left-full" : "max-md:left-0"
      )}
    >
      {/* Logo Header Section */}
      <div className="flex h-16 items-center justify-start border-b border-gray-200 px-4 dark:border-gray-700">
        {collapsed ? (
          <img
            src={logoDark}
            alt="Company Logo"
            className="h-8 w-8 rounded-md object-cover dark:hidden"
          />
        ) : (
          <div className="flex items-center space-x-3">
            <img
              src={logoDark}
              alt="Company Logo"
              className="h-8 w-8 rounded-md object-cover dark:hidden"
            />
            <span className="text-lg font-semibold text-gray-800 dark:hidden">
              Alguidance.lk
            </span>
          </div>
        )}
        <img
          src={logoLight}
          alt="Company Logo"
          className="hidden h-8 w-8 rounded-md object-cover dark:block"
        />
        {!collapsed && (
          <span className="ml-3 hidden text-lg font-semibold text-white dark:block">
              Alguidance.lk
          </span>
        )}
      </div>

      {/* Navigation Section */}
      <div className="flex w-full flex-col gap-y-4 overflow-y-auto overflow-x-hidden px-3 py-6 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
          </div>
        ) : (
          displayTabs.map((section) => (
            <nav
              key={section.title}
              className={cn("mb-2 flex flex-col", collapsed && "md:items-center")}
            >
              <p
                className={cn(
                  "mb-3 px-2 text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400",
                  collapsed && "md:w-12 md:text-center md:text-[9px]"
                )}
              >
                {section.title}
              </p>

              {section.links.map((link) => {
                const isActive = 
                  location.pathname === link.path || 
                  (link.path === "/admin" && location.pathname === "/admin/");
                  
                return (
                  <NavLink
                    key={link.label}
                    to={link.path}
                    onClick={handleLinkClick}
                    className={({ isActive }) =>
                      cn(
                        "group mb-1 flex items-center gap-3 rounded-lg px-3 py-2.5 transition-all duration-200 ease-in-out",
                        collapsed && "md:justify-center md:px-2",
                        isActive
                          ? "bg-blue-50 text-blue-600 font-medium dark:bg-blue-900/30 dark:text-blue-400"
                          : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                      )
                    }
                  >
                    <span className={cn(
                      "flex h-6 w-6 items-center justify-center rounded-md text-current", 
                      isActive ? "text-blue-600 dark:text-blue-400" : "text-gray-500 dark:text-gray-400",
                      "group-hover:text-blue-600 dark:group-hover:text-blue-400"
                    )}>
                      <link.icon size={20} className="flex-shrink-0" />
                    </span>
                    
                    {!collapsed && (
                      <span className={cn(
                        "text-sm font-medium whitespace-nowrap transition-colors",
                        isActive ? "text-blue-600 dark:text-blue-400" : "text-gray-700 dark:text-gray-300",
                        "group-hover:text-blue-600 dark:group-hover:text-blue-400"
                      )}>
                        {link.label}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </nav>
          ))
        )}
      </div>
      
      
    </aside>
  );
});

Sidebar.displayName = "Sidebar";

Sidebar.propTypes = {
  collapsed: PropTypes.bool,
  setCollapsed: PropTypes.func.isRequired,
};